import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { UsuarioWorkflow } from './models';
import workflowService from './workflowService';

interface Cargo {
  id: number;
  nivel: number;
  nome: string;
  funcao: string;
  permissoes: string[];
  ativo: boolean;
}

const emptyForm = { nome: '', funcao: '', nivel: '', permissoes: '' };

const CadastroCargosPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 2;
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [usuariosWorkflow, setUsuariosWorkflow] = useState<UsuarioWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Cargo | null>(null);
  const [form, setForm] = useState({ nome: '', funcao: '', permissoes: '' });
  const [toast, setToast] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [novoForm, setNovoForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState<Cargo | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4500); };

  const fetchDados = () => {
    setLoading(true);
    Promise.all([
      workflowService.listarCargos(),
      workflowService.listarUsuarios(),
    ])
      .then(([dadosCargos, dadosUsuarios]) => {
        const mapped: Cargo[] = dadosCargos.map(c => ({
          id: c.cargoId,
          nivel: c.nivelHierarquico,
          nome: c.nomeCargo,
          funcao: c.funcaoDescricao ?? '',
          permissoes: Array.isArray(c.permissoes) ? c.permissoes : [],
          ativo: c.ativo,
        }));
        setCargos(mapped.sort((a, b) => a.nivel - b.nivel));
        setUsuariosWorkflow(dadosUsuarios);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDados(); }, []);

  const abrirEditar = (c: Cargo) => {
    setEditing(c);
    setForm({ nome: c.nome, funcao: c.funcao, permissoes: c.permissoes.join(', ') });
  };

  const salvar = () => {
    if (!form.nome.trim() || !editing) return;
    workflowService
      .atualizarCargo(editing.id, {
        nomeCargo: form.nome.trim(),
        funcaoDescricao: form.funcao.trim() || undefined,
        permissoes: form.permissoes.split(',').map(p => p.trim()).filter(Boolean),
      })
      .then(() => {
        showToast(`Cargo N${editing.nivel} atualizado. Registro SOX de alteração hierárquica gerado automaticamente.`);
        setEditing(null);
        fetchDados();
      })
      .catch(() => showToast('Erro ao salvar cargo. Tente novamente.'));
  };

  const criarCargo = () => {
    if (!novoForm.nome.trim() || !novoForm.nivel) return;
    workflowService
      .criarCargo({
        nomeCargo: novoForm.nome.trim(),
        nivelHierarquico: Number(novoForm.nivel),
        funcaoDescricao: novoForm.funcao.trim() || undefined,
        permissoes: novoForm.permissoes.split(',').map(p => p.trim()).filter(Boolean),
      })
      .then(() => {
        fetchDados();
        showToast('Cargo criado. Log SOX registrado.');
        setShowModal(false);
        setNovoForm(emptyForm);
      })
      .catch(() => {});
  };

  const excluir = (c: Cargo) => {
    const vinculados = usuariosWorkflow.filter(u => u.nivel === c.nivel).length;
    if (vinculados > 0) {
      showToast(`Não é possível excluir: ${vinculados} usuário(s) vinculado(s) a este cargo.`);
      setConfirmDelete(null);
      return;
    }
    workflowService
      .excluirCargo(c.id)
      .then(() => {
        fetchDados();
        showToast('Cargo removido.');
        setConfirmDelete(null);
      })
      .catch(() => {});
  };

  return <div className='wf-page'>
    <div className='wf-breadcrumb'>Home › Governança de TI › Cadastro de Cargos e Níveis</div>
    <div className='wf-header'>
      <div><h1>Cadastro de Cargos e Níveis Hierárquicos</h1><p>Configure os níveis do workflow: nome do cargo, função e permissões.</p></div>
      <div className='wf-header__actions'>
        {fromOnboarding && (
          <button
            className='wf-btn wf-btn--secondary'
            onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
          >
            ← Onboarding Etapa {onboardingEtapa}
          </button>
        )}
        {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
        <span className='wf-level7'>Acesso exclusivo Nível 7 — TI</span>
        <button className='wf-btn wf-btn--primary' onClick={() => { setNovoForm(emptyForm); setShowModal(true); }}>+ Novo cargo</button>
      </div>
    </div>

    {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

    {loading ? (
      <p className='wf-count' style={{ textAlign: 'center', padding: 32 }}>Carregando...</p>
    ) : (
      <div className='wf-cargo-grid'>
        {cargos.length === 0 && (
          <p className='wf-count' style={{ padding: 24, color: '#a1a5b7' }}>Nenhum cargo cadastrado.</p>
        )}
        {cargos.map(c => {
          const usuarios = usuariosWorkflow.filter(u => u.nivel === c.nivel);
          const isEditing = editing?.id === c.id;
          return <section key={c.id} className={`wf-card wf-cargo-card ${isEditing ? 'is-editing' : ''}`}>
            <div className='wf-cargo-header'>
              <div className='wf-nivel-badge wf-nivel-badge--lg'>N{c.nivel}</div>
              <div style={{ flex: 1 }}>
                {isEditing
                  ? <input value={form.nome} onChange={e => { const value_ = e.target.value; setForm(f => ({ ...f, nome: value_ })); }} style={{ width: '100%', fontWeight: 700, fontSize: 14, border: '1px solid #3699ff', borderRadius: 6, padding: '4px 8px' }} />
                  : <strong>{c.nome}</strong>}
                <span>{c.funcao}</span>
              </div>
              {isEditing
                ? <div style={{ display: 'flex', gap: 6 }}>
                    <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={salvar}>Salvar</button>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setEditing(null)}>Cancelar</button>
                  </div>
                : <div style={{ display: 'flex', gap: 6 }}>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => abrirEditar(c)}>Editar</button>
                    <button className='wf-btn wf-btn--danger-outline wf-btn--sm' onClick={() => setConfirmDelete(c)}>Excluir</button>
                  </div>}
            </div>

            {isEditing && <div className='wf-form-grid wf-form-grid--one' style={{ marginTop: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#5e6278', display: 'flex', flexDirection: 'column', gap: 6 }}>Função no workflow<input value={form.funcao} onChange={e => { const value_ = e.target.value; setForm(f => ({ ...f, funcao: value_ })); }} /></label>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#5e6278', display: 'flex', flexDirection: 'column', gap: 6 }}>Permissões (separadas por vírgula)<textarea rows={3} value={form.permissoes} onChange={e => { const value_ = e.target.value; setForm(f => ({ ...f, permissoes: value_ })); }} /></label>
            </div>}

            {!isEditing && <>
              <div className='wf-cargo-perms'>
                {c.permissoes.map(p => <span key={p} className='wf-cargo-perm'>{p}</span>)}
              </div>
              <div className='wf-cargo-users'>
                <span>Usuários neste nível: <strong>{usuarios.length}</strong></span>
                {usuarios.length === 0
                  ? <span className='wf-danger-text'>Nenhum usuário — cargo órfão!</span>
                  : usuarios.map(u => <span key={u.id} className={`wf-cargo-user ${u.ativo ? '' : 'is-inativo'}`}>{u.nome}</span>)}
              </div>
            </>}
          </section>;
        })}
      </div>
    )}

    {/* Modal novo cargo */}
    {showModal && <div className='wf-modal-bg'><div className='wf-modal'>
      <div className='wf-modal__header'>
        <div><h2>Novo cargo</h2><p>Campos obrigatórios marcados com *</p></div>
        <button onClick={() => setShowModal(false)}>×</button>
      </div>
      <div className='wf-modal__body'>
        <div className='wf-form-grid'>
          <label>Nome do cargo *
            <input value={novoForm.nome} onChange={e => { const value_ = e.target.value; setNovoForm(f => ({ ...f, nome: value_ })); }} placeholder='Ex: Auditor Interno' />
          </label>
          <label>Nível hierárquico *
            <select value={novoForm.nivel} onChange={e => { const value_ = e.target.value; setNovoForm(f => ({ ...f, nivel: value_ })); }}>
              <option value=''>— Selecione —</option>
              {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>N{n}</option>)}
            </select>
          </label>
          <label>Função no workflow
            <input value={novoForm.funcao} onChange={e => { const value_ = e.target.value; setNovoForm(f => ({ ...f, funcao: value_ })); }} placeholder='Ex: Revisão de conformidade' />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: '#5e6278', marginTop: 14 }}>
          Permissões (separadas por vírgula)
          <textarea rows={3} value={novoForm.permissoes} onChange={e => { const value_ = e.target.value; setNovoForm(f => ({ ...f, permissoes: value_ })); }} placeholder='Ex: Revisar conciliação, Solicitar correção' />
        </label>
      </div>
      <div className='wf-modal__footer'>
        <button className='wf-btn wf-btn--secondary' onClick={() => setShowModal(false)}>Cancelar</button>
        <button className='wf-btn wf-btn--primary' disabled={!novoForm.nome.trim() || !novoForm.nivel} onClick={criarCargo}>Cadastrar cargo</button>
      </div>
    </div></div>}

    {/* Modal confirmação de exclusão */}
    {confirmDelete && <div className='wf-modal-bg'><div className='wf-modal'>
      <div className='wf-modal__header'>
        <div><h2>Confirmar exclusão</h2><p>Esta ação será registrada no log de auditoria SOX.</p></div>
        <button onClick={() => setConfirmDelete(null)}>×</button>
      </div>
      <div className='wf-modal__body'>
        <div className='wf-sod' style={{ background: '#fff5f8', borderColor: '#f64e60' }}>
          <b style={{ color: '#f64e60' }}>!</b>
          <div>
            <strong>Excluir cargo: {confirmDelete.nome} (N{confirmDelete.nivel})</strong>
            <p>
              Usuários vinculados ({usuariosWorkflow.filter(u => u.nivel === confirmDelete.nivel).length}) a este nível ficarão sem cargo atribuído.
              {usuariosWorkflow.filter(u => u.nivel === confirmDelete.nivel).length > 0
                ? ' Remova os vínculos de usuário antes de excluir este cargo.'
                : ' Confirme apenas se o cargo não estiver mais em uso.'}
            </p>
          </div>
        </div>
      </div>
      <div className='wf-modal__footer'>
        <button className='wf-btn wf-btn--secondary' onClick={() => setConfirmDelete(null)}>Cancelar</button>
        <button
          className='wf-btn wf-btn--danger-outline'
          disabled={usuariosWorkflow.filter(u => u.nivel === confirmDelete.nivel).length > 0}
          onClick={() => excluir(confirmDelete)}
        >
          Confirmar exclusão
        </button>
      </div>
    </div></div>}
  </div>;
};

export default CadastroCargosPage;
